// idleState — tracks the LOCAL user's activity to derive a presence
// status of 'online' (recently active + tab visible) vs 'idle' (no
// activity for 5 min, or the tab is hidden).
//
// This is the SOURCE of the `status` field the realtime provider tracks
// into the Supabase presence channel (see sdkRealtimeProvider.js, the
// `op === 'presence'` branch). presence.js then reads it back off each
// peer's tracked meta and surfaces it as `onlineUsers[].status`, which
// the /team-and-agents person nodes render as a green (online) vs amber
// (idle) dot — mirroring the existing agent idle/online indicators.
//
// Single shared listener set per tab, booted once on first use. Passive
// document listeners reset an idle timer; after IDLE_AFTER_MS of no
// activity (or `document.hidden`) the status flips to 'idle' and every
// registered subscriber is notified. SSR-guarded — on the server it
// reports a stable 'online' and never touches `document`.

// FA204 / frank trap — `status`/`timer`/`booted`/`subscribers` are genuine
// cross-call state (no el/root argument to hang them on; the whole point is
// ONE shared listener set + status per tab, across every independent
// caller of either export below), so they're re-homed onto
// `window.__idleState` per the pointerCapability.js template. The two
// exports below share the boot/status apparatus, so each carries its own
// self-contained copy of the full state-machine builder (mirrors
// openInSlot.js's "shared by 2 exports, duplicated into both" shape) rather
// than depending on a bare module-scope helper that breaks post-frank-
// serialization — `w.__idleState` itself is what keeps the two copies
// pointed at the SAME live state object at runtime.

// Current local presence status: 'online' | 'idle'. SSR returns the
// stable default so server-rendered tracks never claim idle.
export function getPresenceStatus() {
  const w = typeof window !== 'undefined' ? window : globalThis
  if (!w.__idleState) {
    const IDLE_AFTER_MS = 5 * 60 * 1000
    const isBrowser = typeof document !== 'undefined' && typeof window !== 'undefined'
    const state = {
      status: 'online',
      timer: null,
      booted: false,
      subscribers: new Set(),
      isBrowser
    }

    const setStatus = (next) => {
      if (next === state.status) return
      state.status = next
      for (const cb of state.subscribers) {
        try {
          cb(state.status)
        } catch (err) {
          console.warn('[idleState] subscriber threw:', err?.message || err)
        }
      }
    }
    const clearTimer = () => {
      if (state.timer != null) {
        clearTimeout(state.timer)
        state.timer = null
      }
    }
    const armTimer = () => {
      clearTimer()
      state.timer = setTimeout(() => setStatus('idle'), IDLE_AFTER_MS)
    }
    // Any user interaction marks active and restarts the countdown —
    // unless the tab is currently hidden, in which case we stay idle
    // until it's visible again.
    const onActivity = () => {
      if (isBrowser && document.hidden) {
        setStatus('idle')
        clearTimer()
        return
      }
      setStatus('online')
      armTimer()
    }
    const onVisibilityChange = () => {
      if (document.hidden) {
        setStatus('idle')
        clearTimer()
      } else {
        onActivity()
      }
    }
    // Idempotent boot — attaches the single shared listener set the first
    // time anyone reads the status or subscribes. Safe to call repeatedly.
    state.boot = () => {
      if (state.booted || !isBrowser) return
      state.booted = true
      const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'pointerdown', 'wheel']
      for (const evt of ACTIVITY_EVENTS) {
        document.addEventListener(evt, onActivity, { passive: true })
      }
      document.addEventListener('visibilitychange', onVisibilityChange, { passive: true })
      // Seed the initial countdown so a tab that's opened and left
      // untouched eventually flips to idle on its own.
      if (document.hidden) setStatus('idle')
      else armTimer()
    }
    w.__idleState = state
  }

  const state = w.__idleState
  if (!state.isBrowser) return 'online'
  state.boot()
  return state.status
}

// Register a callback fired whenever the local status FLIPS (online↔idle).
// Returns an unsubscribe fn. No-op on the server (returns a noop unsub).
export function onPresenceStatusChange(cb) {
  const w = typeof window !== 'undefined' ? window : globalThis
  if (!w.__idleState) {
    const IDLE_AFTER_MS = 5 * 60 * 1000
    const isBrowser = typeof document !== 'undefined' && typeof window !== 'undefined'
    const state = {
      status: 'online',
      timer: null,
      booted: false,
      subscribers: new Set(),
      isBrowser
    }

    const setStatus = (next) => {
      if (next === state.status) return
      state.status = next
      for (const c of state.subscribers) {
        try {
          c(state.status)
        } catch (err) {
          console.warn('[idleState] subscriber threw:', err?.message || err)
        }
      }
    }
    const clearTimer = () => {
      if (state.timer != null) {
        clearTimeout(state.timer)
        state.timer = null
      }
    }
    const armTimer = () => {
      clearTimer()
      state.timer = setTimeout(() => setStatus('idle'), IDLE_AFTER_MS)
    }
    const onActivity = () => {
      if (isBrowser && document.hidden) {
        setStatus('idle')
        clearTimer()
        return
      }
      setStatus('online')
      armTimer()
    }
    const onVisibilityChange = () => {
      if (document.hidden) {
        setStatus('idle')
        clearTimer()
      } else {
        onActivity()
      }
    }
    state.boot = () => {
      if (state.booted || !isBrowser) return
      state.booted = true
      const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'pointerdown', 'wheel']
      for (const evt of ACTIVITY_EVENTS) {
        document.addEventListener(evt, onActivity, { passive: true })
      }
      document.addEventListener('visibilitychange', onVisibilityChange, { passive: true })
      if (document.hidden) setStatus('idle')
      else armTimer()
    }
    w.__idleState = state
  }

  const state = w.__idleState
  if (typeof cb !== 'function' || !state.isBrowser) return () => {}
  state.boot()
  state.subscribers.add(cb)
  return () => {
    state.subscribers.delete(cb)
  }
}
